import axios from "axios";

// Same-origin /api avoids cross-origin preflights stripped by the Emergent
// ingress. REACT_APP_BACKEND_URL is used only when the bundle is loaded
// outside a browser (SSR / tooling).
const isBrowser = typeof window !== "undefined";
export const API_BASE = isBrowser
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Global 401 auto-refresh interceptor
//
// Many protected endpoints (e.g. `/dashboard/master-summary`) return 401 with
// `detail: "Not authenticated"` whenever the access cookie is missing or has
// expired. Without this interceptor the raw backend error text leaks into UI
// state (e.g. `setErr(apiErrorMessage(e))`) and the user sees a literal
// "Not authenticated" message on what should be a protected page.
//
// Behaviour:
//   1. On a 401, attempt `POST /auth/refresh` exactly once (using the long-
//      lived refresh cookie).
//   2. If the refresh succeeds, replay the original request.
//   3. If the refresh fails, dispatch a global `lifted:auth-expired` event so
//      AuthContext can clear local state and ProtectedRoute can redirect to
//      `/auth/login`. The original 401 then propagates to the caller.
//
// The /auth/login, /auth/refresh and /auth/me endpoints are excluded from the
// auto-refresh path so we don't infinite-loop during initial hydration or a
// failed sign-in.
// ---------------------------------------------------------------------------
const REFRESH_EXEMPT = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/me"];

let refreshInFlight = null;

function shouldSkipRefresh(url = "") {
  return REFRESH_EXEMPT.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (status !== 401 || original._retried || shouldSkipRefresh(original.url || "")) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      // De-dupe concurrent refresh attempts.
      if (!refreshInFlight) {
        refreshInFlight = api.post("/auth/refresh").finally(() => {
          refreshInFlight = null;
        });
      }
      await refreshInFlight;
      return api(original);
    } catch (refreshErr) {
      if (isBrowser) {
        window.dispatchEvent(new CustomEvent("lifted:auth-expired"));
      }
      return Promise.reject(error);
    }
  },
);

export function apiErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  // Structured FastAPI HTTPException(422, {error, message, ...}) — show the
  // human-readable `message` field; fall back to `msg`, then raw string.
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return JSON.stringify(detail);
}

export default api;
